import { DragAndDrop } from '@progress/kendo-react-common';
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { EmployeeTemplateOptionModel } from '../../../../../models/EmployeeTemplateOptionModel';
import { inRange } from '../helpers';
import { DirectionContext, DragContext, DropContext } from './DragContext';
import { DraggableListItem } from './DraggableListItem';
import { DroppableList } from './DroppableList';
import { OptionPanel } from './OptionPanel';

interface OptionsTemplateProps {
  options: EmployeeTemplateOptionModel[];
  saveOptionNewName: (optionId: number, optionName: string) => void;
  removeOption: (option: EmployeeTemplateOptionModel) => void;
  optionsChanged: (options: EmployeeTemplateOptionModel[]) => void;
}

export default function OptionsTemplate(props: Readonly<OptionsTemplateProps>) {
  const { options, saveOptionNewName, removeOption, optionsChanged } = props;
  const dropTimeout = useRef<number>();

  const [drag, setDrag] = useState<{
    element: RefObject<HTMLElement | null>;
    data: EmployeeTemplateOptionModel;
    saveOptionNewName: (optionId: number, optionName: string) => void;
    removeOption: (option: EmployeeTemplateOptionModel) => void;
  } | null>(null);

  const [drop, setDrop] = useState<{
    element: RefObject<HTMLElement | null>;
    data: EmployeeTemplateOptionModel;
    saveOptionNewName: (optionId: number, optionName: string) => void;
    removeOption: (option: EmployeeTemplateOptionModel) => void;
  } | null>(null);

  const [direction, setDirection] = useState<'before' | 'after'>('before');

  const handleDropChange = useCallback(
    (
      newDrop: {
        element: RefObject<HTMLElement | null>;
        data: EmployeeTemplateOptionModel;
        saveOptionNewName: (optionId: number, optionName: string) => void;
        removeOption: (option: EmployeeTemplateOptionModel) => void;
      } | null,
    ) => {
      if (!newDrop) {
        dropTimeout.current = window.setTimeout(() => {
          setDrop(newDrop);
        }, 50);
      } else {
        clearTimeout(dropTimeout.current);
        setDrop(newDrop);
      }
    },
    [],
  );

  const handleItemDrop = useCallback(() => {
    if (!drag || !drop) {
      return;
    }
    const newData = options.slice();
    const oldIndex = newData.findIndex(d => d.optionId === drag.data.optionId);
    const dataItem = newData[oldIndex];

    if (dataItem) {
      newData.splice(oldIndex, 1);

      const newIndex = inRange(
        0,
        newData.length,
        newData.findIndex(d => d.optionId === drop.data.optionId) +
          (direction === 'after' ? 1 : 0),
      );
      newData.splice(newIndex, 0, dataItem);

      optionsChanged(
        newData.map(d => {
          return { ...d, order: newData.indexOf(d) };
        }),
      );
    }
  }, [direction, drag, drop, options, optionsChanged]);

  const handleListDrop = useCallback(() => {
    if (!drag || !drop) {
      return;
    }
    const newData = options.slice();
    const oldIndex = newData.findIndex(d => d.optionId === drag.data.optionId);
    const dataItem = newData[oldIndex];
    if (dataItem) {
      newData.splice(newData.length - 1, 0, newData.splice(oldIndex, 1)[0]);
      optionsChanged(newData);
    }
  }, [drag, drop, options, optionsChanged]);
  return (
    <DragContext.Provider value={useMemo(() => [drag, setDrag], [drag])}>
      <DropContext.Provider
        value={useMemo(
          () => [drop, handleDropChange],
          [drop, handleDropChange],
        )}
      >
        <DirectionContext.Provider
          value={useMemo(() => [direction, setDirection], [direction])}
        >
          <DragAndDrop>
            <DroppableList
              onDrop={handleListDrop}
              data={{
                optionId: drag?.data.optionId ?? 0,
                defaultName: drag?.data.defaultName ?? '',
                name: drag?.data.name ?? '',
                isRequired: drag?.data.isRequired ?? false,
                itSet: drag?.data.itSet ?? false,
                order: drag?.data.order ?? -1,
              }}
              saveOptionNewName={drag?.saveOptionNewName}
              removeOption={drag?.removeOption}
            >
              {options.map(option => (
                <DraggableListItem
                  onDrop={handleItemDrop}
                  key={option.optionId}
                  data={option}
                >
                  <OptionPanel
                    key={option.optionId}
                    option={option}
                    saveOptionNewName={saveOptionNewName}
                    removeOption={removeOption}
                  />
                </DraggableListItem>
              ))}
            </DroppableList>
          </DragAndDrop>
        </DirectionContext.Provider>
      </DropContext.Provider>
    </DragContext.Provider>
  );
}
